import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Auth } from '../../../components/modules/API/Auth/auth'
import { Defaults } from '../../../components/modules/API/Defaults/defaults'
import { handleNotifications } from '../../../components/generic/utils/handleNotifications'
import { IRegistrationPostData } from '../../../components/modules/API/Auth/models/authDataModel'
import { loadDefaults } from '../../../components/modules/requests/loadDefaults'
import {
  LOAD_DEFAULTS,
  LOAD_MODULES,
  LOGIN_SUCCESS,
  TABLE_OF_TABLES,
} from '../../../store/actions/actionsTypes'
import { instance } from '../../../components/modules/API/config'
import useDebounce from '../../../components/main/second/mainbar/seller/Helpers/useDebounce'
import { UseRegisterFormReturn } from './RegisterForm.types'

const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{4,}$/

export const useRegisterForm = (): UseRegisterFormReturn => {
  const dispatch = useDispatch()
  const location = useLocation()
  const inviteToken = location.search.split('=')[1] || null

  const [tel, setTel] = useState('')
  const [userName, setUserName] = useState('')
  const [isExistUserName, setIsExistUserName] = useState(false)
  const [debouncedUserName, setDebouncedUserName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isShowSmsStep, setIsShowSmsStep] = useState(false)
  const [codeSMS, setCodeSMS] = useState('')
  const [isAwaitingCodeVerifying, setIsAwaitingCodeVerifying] = useState(false)
  const [isMatchCode, setIsMatchCode] = useState(false)

  const debounced = useDebounce(userName, 2000)

  useEffect(() => {
    if (debounced.length < 3) return
    setDebouncedUserName(debounced)
  }, [debounced])

  useEffect(() => {
    if (!debouncedUserName) return
    ;(async () => {
      try {
        await Auth.checkUserName(debouncedUserName)
        setIsExistUserName(false)
      } catch (error: any) {
        if (error?.response?.data?.error === 'User already exists') {
          setIsExistUserName(true)
        }
      }
    })()
  }, [debouncedUserName])

  const handleUserName = (e: ChangeEvent<HTMLInputElement>) => {
    setIsExistUserName(false)
    setUserName(e.target.value)
  }
  const handlePassword = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)
  const handleConfirmPassword = (e: ChangeEvent<HTMLInputElement>) =>
    setConfirmPassword(e.target.value)

  const handleTelephone = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    if (!v[0]) return setTel('')
    if (v[0] !== '0') return
    if (v.length > 10) return
    setTel(v)
  }

  const handleCodeSms = (code: string) => setCodeSMS(code)

  const sendSms = async () => {
    if (tel.length !== 10) return
    try {
      await Auth.sendSMS(tel)
      setIsShowSmsStep(true)
    } catch (error: any) {
      handleNotifications(error?.response?.data?.error || 'שגיאה בשליחת SMS', { type: 'error' })
    }
  }

  const verifySMS = async () => {
    if (codeSMS.length !== 4) return
    setIsAwaitingCodeVerifying(true)
    try {
      const res: { status: string } = await Auth.verifySMS(codeSMS, tel)
      const ok = res.status === 'ok'
      setIsMatchCode(ok)
      if (ok) {
        handleNotifications('קוד נכון', { type: 'success' })
        setIsShowSmsStep(false)
      }
    } catch (error: any) {
      if (error?.response?.status === 405) {
        handleNotifications(error.response.data.error, { type: 'error' })
      }
    } finally {
      setIsAwaitingCodeVerifying(false)
    }
  }

  const cancelSmsStep = () => {
    setIsShowSmsStep(false)
    setCodeSMS('')
  }

  const handleSubmit = async (e: FormEvent | React.MouseEvent) => {
    e.preventDefault()
    if (!isMatchCode || isAlertPassword || isExistUserName) return
    try {
      const dataPost: IRegistrationPostData = {
        Code: codeSMS,
        Password: password,
        Phone: tel,
        UserID: userName,
        Token: inviteToken || '',
        FullName: userName,
      }
      const res = await Auth.registration(dataPost)
      localStorage.setItem('user', JSON.stringify(res.token))
      ;(instance as any).updateToken = res.token
      dispatch({ type: LOGIN_SUCCESS, payload: res })
      const modules = await Defaults.getAvailableModules()
      dispatch({ type: LOAD_MODULES, payload: modules })
      await loadDefaults(res.token, LOAD_DEFAULTS, TABLE_OF_TABLES, dispatch)
    } catch (error: any) {
      handleNotifications(error?.response?.data?.error || 'שגיאה בהרשמה', { type: 'error' })
    }
  }

  const isUserNameValid = userName.trim().length >= 3
  const isPasswordValid = passwordRegex.test(password)
  const isPasswordMismatch =
    !!password && !!confirmPassword && password !== confirmPassword
  const isAlertPassword = isPasswordMismatch || (!!password && !isPasswordValid)
  const isAllowSendSms =
    isUserNameValid &&
    !isExistUserName &&
    isPasswordValid &&
    !isPasswordMismatch &&
    tel.length === 10 &&
    !isMatchCode
  const isAllowRegister = !isAlertPassword && !isExistUserName && isMatchCode
  const restDigits = 10 - tel.length

  return {
    userName,
    isExistUserName,
    isUserNameValid,
    password,
    confirmPassword,
    isAlertPassword,
    isPasswordValid,
    isPasswordMismatch,
    tel,
    restDigits,
    isShowSmsStep,
    codeSMS,
    isAwaitingCodeVerifying,
    isMatchCode,
    isAllowSendSms,
    isAllowRegister,
    inviteToken,
    handleUserName,
    handlePassword,
    handleConfirmPassword,
    handleTelephone,
    handleCodeSms,
    sendSms,
    verifySMS,
    cancelSmsStep,
    handleSubmit,
  }
}
